import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SessionInfo, SessionTeam, SessionUser } from '@kolectra/shared/schemas';
import type { PermissionAtom } from '@kolectra/shared/permissions';
import { authApi, setRefreshHandler } from './lib/api-client.js';
import {
  clearTokens,
  getRefreshToken,
  loadTokens,
  setTokens,
} from './lib/token-store.js';

/**
 * Real auth for the web app — replaces the stub previewer. Tokens live in the
 * token-store (localStorage); the access token is refreshed proactively on a
 * timer and reactively on a 401 (via the api-client's refresh handler). The
 * `useWebAuth` / `useWebPerms` surface is kept compatible with the stub so the
 * pages need no churn: `account.{user,team,languages}` resolve to the live
 * session + the active team membership.
 */

const DEVICE_LABEL = 'Kolectra Web';
const ACTIVE_TEAM_KEY = 'kolectra.web.activeTeam';

type Status = 'loading' | 'signedIn' | 'signedOut';

interface AuthContextValue {
  status: Status;
  session: SessionInfo | null;
  activeTeamId: string | null;
  setActiveTeamId: (id: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [activeTeamId, setActiveTeamIdState] = useState<string | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimer = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = undefined;
  };

  const hardSignOut = useCallback(() => {
    clearTimer();
    clearTokens();
    setSession(null);
    setStatus('signedOut');
  }, []);

  // Refresh the access token ~1 min before it expires.
  const scheduleRefresh = useCallback((expiresIn: number) => {
    clearTimer();
    const ms = Math.max(5, expiresIn - 60) * 1000;
    refreshTimer.current = setTimeout(() => void doRefresh(), ms);
  }, []);

  const doRefresh = useCallback(async (): Promise<boolean> => {
    const rt = getRefreshToken();
    if (!rt) {
      hardSignOut();
      return false;
    }
    try {
      const tokens = await authApi.refresh(rt);
      setTokens(tokens);
      scheduleRefresh(tokens.expiresIn);
      return true;
    } catch {
      hardSignOut();
      return false;
    }
  }, [hardSignOut, scheduleRefresh]);

  const setActiveTeamId = useCallback((id: string) => {
    setActiveTeamIdState(id);
    try {
      localStorage.setItem(ACTIVE_TEAM_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const pickActiveTeam = useCallback((session: SessionInfo) => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(ACTIVE_TEAM_KEY);
    } catch {
      /* ignore */
    }
    const ids = effectiveWebTeams(session).map((t) => t.id);
    const valid = saved && ids.includes(saved) ? saved : ids[0] ?? null;
    setActiveTeamIdState(valid);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await authApi.signIn(email, password, DEVICE_LABEL);
      const { accessToken, refreshToken, expiresIn, user, teams, project } = result;
      setTokens({ accessToken, refreshToken, expiresIn });
      scheduleRefresh(expiresIn);
      // Keep `project` — a project-only user (observer, project admin) has no team,
      // so dropping it would strand them on the no-access screen until a reload.
      setSession({ user, teams, project });
      pickActiveTeam({ user, teams, project });
      setStatus('signedIn');
    },
    [scheduleRefresh, pickActiveTeam],
  );

  const signOut = useCallback(async () => {
    const rt = getRefreshToken();
    try {
      if (rt) await authApi.signOut(rt);
    } catch {
      /* best-effort; clear locally regardless */
    }
    hardSignOut();
  }, [hardSignOut]);

  // Startup: register the refresh handler, then restore a session if we have a
  // refresh token (refresh once for a fresh access token, then load /me).
  useEffect(() => {
    setRefreshHandler(doRefresh);
    const { refreshToken } = loadTokens();
    if (!refreshToken) {
      setStatus('signedOut');
      return () => setRefreshHandler(null);
    }
    void (async () => {
      const ok = await doRefresh();
      if (!ok) return;
      try {
        const s = await authApi.me();
        setSession(s);
        pickActiveTeam(s);
        setStatus('signedIn');
      } catch {
        hardSignOut();
      }
    })();
    return () => setRefreshHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, session, activeTeamId, setActiveTeamId, signIn, signOut }),
    [status, session, activeTeamId, setActiveTeamId, signIn, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useAuthContext(): AuthContextValue {
  const c = useContext(Ctx);
  if (!c) throw new Error('Auth hooks must be used within <AuthProvider>.');
  return c;
}

/** Raw auth state — for the sign-in gate and the login form. */
export function useAuth(): AuthContextValue {
  return useAuthContext();
}

// --- The page-facing surface (compatible with the former stub) ----------

export interface WebTeam {
  id: string;
  name: string;
  roleName: string;
  languoidCodes: string[];
  permissions: PermissionAtom[];
}

export interface WebAccount {
  user: SessionUser;
  /** The active team membership. */
  team: WebTeam;
  /** All the user's team memberships (for the team switcher). */
  teams: WebTeam[];
  /** Languages the user can work in (active team's languoids). */
  languages: string[];
  /** Role names across memberships (active team first). */
  roleNames: string[];
}

interface WebAuthValue {
  account: WebAccount;
  session: SessionInfo;
  activeTeamId: string;
  setActiveTeamId: (id: string) => void;
  signOut: () => Promise<void>;
}

function toWebTeam(t: SessionTeam): WebTeam {
  return {
    id: t.teamId,
    name: t.name,
    roleName: t.roleName ?? 'Member',
    languoidCodes: t.languoidCodes,
    permissions: t.permissions as PermissionAtom[],
  };
}

/**
 * The teams the web app operates over. A user with real team memberships uses those.
 * A teamless PROJECT worker (e.g. a project-level reviewer with judge/approve assigned to
 * some languages) gets SYNTHESIZED teams from their project — one per project team whose
 * language they're assigned, carrying the project-level permissions — so the normal team
 * app (AppShell + Judgement page) serves them, scoped to their assigned languages. The per-
 * challenge endpoints already accept project members (permissions resolve the project union
 * into each team). Observers/admins (`can_view_project_reports`) use the standalone project
 * dashboard instead (decided in App.tsx), so they never reach this synthesis.
 */
export function effectiveWebTeams(session: SessionInfo): WebTeam[] {
  if (session.teams.length > 0) return session.teams.map(toWebTeam);

  const proj = session.project;
  if (!proj) return [];
  const perms = proj.permissions as PermissionAtom[];
  const assigned = proj.languoidCodes ?? [];
  const scope = (langs: string[]) => (assigned.length === 0 ? langs : langs.filter((l) => assigned.includes(l)));
  return proj.teams
    .filter((t) => assigned.length === 0 || t.languoidCodes.some((l) => assigned.includes(l)))
    .map((t) => ({
      id: t.teamId,
      name: t.name,
      roleName: proj.roleNames[0] ?? 'Project member',
      languoidCodes: scope(t.languoidCodes),
      permissions: perms,
    }));
}

/**
 * The signed-in user + active team. Only valid inside the authed app (the gate
 * guarantees a session before any consumer renders).
 */
export function useWebAuth(): WebAuthValue {
  const { session, activeTeamId, setActiveTeamId, signOut } = useAuthContext();
  if (!session) throw new Error('useWebAuth used while signed out.');

  const teams = effectiveWebTeams(session);
  const active = teams.find((t) => t.id === activeTeamId) ?? teams[0];
  if (!active) throw new Error('Signed-in user has no team memberships.');

  const account: WebAccount = {
    user: session.user,
    team: active,
    teams,
    languages: active.languoidCodes,
    roleNames: [active.roleName, ...teams.filter((t) => t.id !== active.id).map((t) => t.roleName)],
  };

  return { account, session, activeTeamId: active.id, setActiveTeamId, signOut };
}

export function useWebPerms() {
  const { account } = useWebAuth();
  const perms = new Set(account.team.permissions);
  return {
    roleName: account.team.roleName,
    has: (p: PermissionAtom) => perms.has(p),
  };
}
